import { LitElement, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { StateMixin } from "../mixins/state";
import { Routing } from "../mixins/routing";
import { singleton } from "../lib/singleton";
import { app } from "../init";
import { shared } from "../styles";
import { alert } from "./alert-dialog";
import "./spinner";
import "./popover";
import "./scroller";
import "./help";
import { ChangePasswordDialog } from "./change-password-dialog";
import { Checkbox } from "./checkbox";
import { EntityFiltersEl } from "./entity-filters";
import { EnabledFeatures, enabledFeatures } from "../contexts/features-context";
import { consume } from "@lit/context";
import { haveFiltersChanged , AccountDTO, NotificationSettings } from "@pentacode/core";

@customElement("ptc-settings-account")
export class SettingsAccount extends Routing(StateMixin(LitElement)) {
    routePattern = /^settings\/account/;

    get routeTitle() {
        return "Benutzerkonto | Einstellungen";
    }

    get helpPage() {
        return "handbuch/einstellungen/benutzerkonto/";
    }

    @state()
    private _notificationSettings: NotificationSettings = {};

    @query("[name='firstName']")
    private _firstNameInput: HTMLInputElement;

    @query("[name='lastName']")
    private _lastNameInput: HTMLInputElement;

    @query("[name='email']")
    private _emailInput: HTMLInputElement;

    @query("[name='vacationRequests.email']")
    private _vacationRequestsEmailCheckbox: Checkbox;

    @query("[name='vacationRequests.desktop']")
    private _vacationRequestsDesktopCheckbox: Checkbox;

    @query("[name='vacationRequests.mobile']")
    private _vacationRequestsMobileCheckbox: Checkbox;

    @query("[name='vacationRequests.filters']")
    private _vacationRequestsFilters: EntityFiltersEl;

    @query("[name='sickNotes.email']")
    private _sickNotesEmailCheckbox: Checkbox;

    @query("[name='sickNotes.desktop']")
    private _sickNotesDesktopCheckbox: Checkbox;

    @query("[name='sickNotes.mobile']")
    private _sickNotesMobileCheckbox: Checkbox;

    @query("[name='sickNotes.filters']")
    private _sickNotesFilters: EntityFiltersEl;

    @consume({ context: enabledFeatures })
    private _enabledFeatures: EnabledFeatures;

    @state()
    private _loading: boolean = false;

    @singleton("ptc-change-password-dialog")
    private _changePasswordDialog: ChangePasswordDialog;

    private get _accountInfoData() {
        const form = this.renderRoot.querySelector("#accountInfoForm") as HTMLFormElement | null;

        if (!form) {
            return {};
        }

        const data = new FormData(form);

        return {
            firstName: data.get("firstName") as string,
            lastName: data.get("lastName") as string,
            email: data.get("email") as string,
        };
    }

    private get _notificationSettingsData(): NotificationSettings {
        const form = this.renderRoot.querySelector("#notificationsForm") as HTMLFormElement | null;

        if (!form) {
            return {};
        }

        const data = new FormData(form);

        return {
            vacationRequests: {
                email: data.has("vacationRequests.email"),
                pushDesktop: data.has("vacationRequests.desktop"),
                pushMobile: data.has("vacationRequests.mobile"),
                filters: this._vacationRequestsFilters.filters,
            },
            sickNotes: {
                email: data.has("sickNotes.email"),
                pushDesktop: data.has("sickNotes.desktop"),
                pushMobile: data.has("sickNotes.mobile"),
                filters: this._sickNotesFilters.filters,
            },
        };
    }

    private get _hasAccountInfoChanged() {
        return (
            app.account &&
            Object.entries(this._accountInfoData).some(
                ([key, value]) => app.account![key as keyof AccountDTO] !== value
            )
        );
    }

    private get _hasNotificationSettingsChanged() {
        if (!app.profile) {
            return false;
        }
        const notifications = app.profile.notifications;
        return (
            notifications.vacationRequests?.email !== this._vacationRequestsEmailCheckbox?.checked ||
            notifications.vacationRequests?.pushDesktop !== this._vacationRequestsDesktopCheckbox?.checked ||
            notifications.vacationRequests?.pushMobile !== this._vacationRequestsMobileCheckbox?.checked ||
            haveFiltersChanged(
                notifications.vacationRequests?.filters || [],
                this._vacationRequestsFilters?.filters || []
            ) ||
            notifications.sickNotes?.email !== this._sickNotesEmailCheckbox?.checked ||
            notifications.sickNotes?.pushDesktop !== this._sickNotesDesktopCheckbox?.checked ||
            notifications.sickNotes?.pushMobile !== this._sickNotesMobileCheckbox?.checked ||
            haveFiltersChanged(notifications.sickNotes?.filters || [], this._sickNotesFilters?.filters || [])
        );
    }

    protected async handleRoute() {
        // wait for form elements to be rendered
        await this.updateComplete;
        this._resetAccountInfo();
        void this._loadNotificationSettings();
    }

    private _resetAccountInfo() {
        if (!app.account) {
            return;
        }
        this._firstNameInput.value = app.account.firstName;
        this._lastNameInput.value = app.account.lastName;
        this._emailInput.value = app.account.email || "";
        this.requestUpdate();
    }

    private async _loadNotificationSettings() {
        this._loading = true;
        try {
            this._notificationSettings = await app.api.getNotificationSettings();
            this._resetNotificationSettings();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private _resetNotificationSettings() {
        if (!app.profile) {
            return;
        }
        const notifications = this._notificationSettings;
        const vacationRequests = notifications.vacationRequests;
        const sickNotes = notifications.sickNotes;

        this._vacationRequestsEmailCheckbox.checked = vacationRequests?.email || false;
        this._vacationRequestsDesktopCheckbox.checked = vacationRequests?.pushDesktop || false;
        this._vacationRequestsMobileCheckbox.checked = vacationRequests?.pushMobile || false;
        this._vacationRequestsFilters.filters = vacationRequests?.filters || [];

        this._sickNotesEmailCheckbox.checked = sickNotes?.email || false;
        this._sickNotesDesktopCheckbox.checked = sickNotes?.pushDesktop || false;
        this._sickNotesMobileCheckbox.checked = sickNotes?.pushMobile || false;
        this._sickNotesFilters.filters = sickNotes?.filters || [];
        this.requestUpdate();
    }

    private async _submitAccountInfo(e: FocusEvent) {
        e.preventDefault();

        this._loading = true;
        try {
            await app.updateAccount({ ...this._accountInfoData });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _submitnotificationSettings(e: FocusEvent) {
        e.preventDefault();

        this._loading = true;
        try {
            await app.api.updateNotificationSettings(this._notificationSettingsData);
            await this._loadNotificationSettings();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    static styles = [shared, Checkbox.styles];

    render() {
        if (!app.company || !app.account || !app.profile) {
            return html``;
        }

        return html`
            <ptc-scroller class="fullbleed">
                <div style="max-width: 50em; padding: 1em; margin: 0 auto;">
                    <form id="accountInfoForm" @submit=${this._submitAccountInfo} @change=${() => this.requestUpdate()}>
                        <h2>Benutzer</h2>

                        <div class="horizontal spacing layout">
                            <div class="field stretch">
                                <label>Vorname</label>
                                <input type="text" name="firstName" placeholder="Vorname" />
                            </div>
                            <div class="field stretch">
                                <label>Nachname</label>
                                <input type="text" name="lastName" placeholder="Nachname" />
                            </div>
                        </div>

                        <div class="field">
                            <label>Benutzername / Emailadresse</label>
                            <input type="email" name="email" required placeholder="Emailadresse" />
                        </div>

                        <div
                            class="evenly stretching spacing horizontal layout"
                            ?hidden=${!this._hasAccountInfoChanged}
                        >
                            <button class="primary">Speichern</button>
                            <button class="transparent" type="button" @click=${() => this._resetAccountInfo()}>
                                Zurücksetzen
                            </button>
                        </div>
                    </form>

                    <h2>Passwort</h2>

                    <div>
                        <button class="ghost" @click=${() => this._changePasswordDialog.show()}>
                            <i class="lock"></i> Passwort Ändern
                        </button>
                    </div>

                    <h2>Benachrichtigungen</h2>

                    <form
                        id="notificationsForm"
                        @submit=${this._submitnotificationSettings}
                        @change=${() => this.requestUpdate()}
                    >
                        <table class="rounded fill-horizontally">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th><i class="at"></i><br />Email</th>
                                    <th ?hidden=${!this._enabledFeatures.notificationsPushDesktop}>
                                        <i class="desktop"></i><br />Desktop
                                    </th>
                                    <th ?hidden=${!this._enabledFeatures.notificationsPushMobile}>
                                        <i class="mobile"></i><br />Mobil
                                    </th>
                                    <th><i class="filter"></i><br />Filter</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th class="semibold"><i class="comment-question"></i> Urlaubsanfragen</th>
                                    <td class="text-centering">
                                        <ptc-checkbox name="vacationRequests.email"></ptc-checkbox>
                                    </td>
                                    <td
                                        class="text-centering"
                                        ?hidden=${!this._enabledFeatures.notificationsPushDesktop}
                                    >
                                        <ptc-checkbox name="vacationRequests.desktop"></ptc-checkbox>
                                    </td>
                                    <td
                                        class="text-centering"
                                        ?hidden=${!this._enabledFeatures.notificationsPushMobile}
                                    >
                                        <ptc-checkbox name="vacationRequests.mobile"></ptc-checkbox>
                                    </td>
                                    <td>
                                        <ptc-entity-filters
                                            name="vacationRequests.filters"
                                            class="focus-container"
                                            hideFilterIcon
                                            hideEmployeeCount
                                            .filterTypes=${["venue", "department", "position", "employeeId"] as const}
                                            @change=${() => this.requestUpdate()}
                                        ></ptc-entity-filters>
                                    </td>
                                </tr>
                                <tr>
                                    <th class="semibold"><i class="face-thermometer"></i> Krankmeldungen</th>
                                    <td class="text-centering">
                                        <ptc-checkbox name="sickNotes.email"></ptc-checkbox>
                                    </td>
                                    <td
                                        class="text-centering"
                                        ?hidden=${!this._enabledFeatures.notificationsPushDesktop}
                                    >
                                        <ptc-checkbox name="sickNotes.desktop"></ptc-checkbox>
                                    </td>
                                    <td
                                        class="text-centering"
                                        ?hidden=${!this._enabledFeatures.notificationsPushMobile}
                                    >
                                        <ptc-checkbox name="sickNotes.mobile"></ptc-checkbox>
                                    </td>
                                    <td>
                                        <ptc-entity-filters
                                            name="sickNotes.filters"
                                            class="focus-container"
                                            hideFilterIcon
                                            hideEmployeeCount
                                            .filterTypes=${["venue", "department", "position", "employeeId"] as const}
                                            @change=${() => this.requestUpdate()}
                                        ></ptc-entity-filters>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div
                            class="evenly stretching spacing horizontal layout"
                            ?hidden=${!this._hasNotificationSettingsChanged}
                        >
                            <button class="primary">Speichern</button>
                            <button class="transparent" type="button" @click=${() => this._resetNotificationSettings()}>
                                Zurücksetzen
                            </button>
                        </div>
                    </form>
                </div>
            </ptc-scroller>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-spinner ?active=${this._loading}></ptc-spinner>
            </div>
        `;
    }
}
